import { useState } from 'react';

import { useAppDispatch } from '../../store/store';
import { supabase } from '../../lib/supabase';
import { authActions } from '../../store/slices/auth';
import { IDADE_MINIMA, ehMaiorDeIdade, idadeEmAnos } from '../../utils/age';
import { guardarConsentimentoPendente } from '../../hooks/useConsent';
import { authError, erroDoEmail } from './erros';
import type { Ir } from '../porta';

// O CADASTRO — a lógica do antigo `pages/Signup`, sem o desenho.
// 'form' = dados do cadastro; 'code' = confirmar o e-mail com o código enviado.
// Mora no núcleo desde 01/11/2026 (docs/PLANO-APP-NATIVO.md): a web e o app usam a mesma regra.

export { IDADE_MINIMA };

export type CampoDoCadastro = 'nome' | 'email' | 'senha' | 'nascimento' | 'aceite';
export type ErrosDoCadastro = Partial<Record<CampoDoCadastro, string>>;

/**
 * `emailInicial`: o e-mail que já chega escolhido. A web lê o do convite (`?email=`) e, depois, o
 * que a pessoa digitou na landing; o app, o do convite.
 */
export const useCadastro = ({ ir: navigate, emailInicial = '' }: { ir: Ir; emailInicial?: string }) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(emailInicial);
  const [password, setPassword] = useState('');
  // EM DOIS PASSOS, como o entrar (pedido do Lucas, 02/10/2026): primeiro o e-mail, depois o resto
  // (nome, senha, nascimento e os aceites), com as outras formas de entrar à vista no primeiro.
  // Com o e-mail já trazido (o convite, o cartão da landing), começa no segundo.
  const [etapa, setEtapa] = useState<'email' | 'dados'>(() => (email && !erroDoEmail(email) ? 'dados' : 'email'));
  const [birthDate, setBirthDate] = useState('');
  const [aceite, setAceite] = useState(false);
  const [comunicacoes, setComunicacoes] = useState(false);
  const [loading, setLoading] = useState(false);
  // O erro de cada campo aparece embaixo dele (validado ao sair do campo e no envio); `error` fica
  // para o que vem do servidor.
  const [erros, setErros] = useState<ErrosDoCadastro>({});
  const [error, setError] = useState<string | null>(null);

  type Valores = { name: string; email: string; password: string; birthDate: string; aceite: boolean };
  const valores: Valores = { name, email, password, birthDate, aceite };

  const conferir = (campo: CampoDoCadastro, v: Valores = valores): string | undefined => {
    if (campo === 'nome') return v.name.trim() ? undefined : 'Informe seu nome.';
    if (campo === 'email') return erroDoEmail(v.email);
    if (campo === 'senha') return v.password.length < 6 ? 'A senha precisa ter ao menos 6 caracteres.' : undefined;
    if (campo === 'nascimento') {
      // Retorno imediato só para não criar uma conta que nasceria bloqueada; quem decide de fato é
      // a edge function, no servidor.
      if (!v.birthDate) return 'Informe sua data de nascimento.';
      if (idadeEmAnos(v.birthDate) === null) return 'Essa data de nascimento não existe.';
      if (!ehMaiorDeIdade(v.birthDate)) return `A Maestra é destinada a maiores de ${IDADE_MINIMA} anos.`;
      return undefined;
    }
    return v.aceite ? undefined : 'É preciso aceitar os Termos de Uso e a Política de Privacidade.';
  };

  const validarCampo = (campo: CampoDoCadastro) => setErros((e) => ({ ...e, [campo]: conferir(campo) }));

  // O primeiro erro aparece ao sair do campo ou no envio; depois, com o erro à mostra, cada mudança
  // confere de novo, e ele some assim que o campo fica certo.
  const reconferir = (campo: CampoDoCadastro, mudanca: Partial<Valores>) => {
    if (erros[campo]) setErros((e) => ({ ...e, [campo]: conferir(campo, { ...valores, ...mudanca }) }));
  };

  const cadastrar = async () => {
    setError(null);
    const todos = (['nome', 'email', 'senha', 'nascimento', 'aceite'] as const)
      .reduce<ErrosDoCadastro>((acc, c) => ({ ...acc, [c]: conferir(c) }), {});
    setErros(todos);
    if (Object.values(todos).some(Boolean)) return;

    // Guarda o que foi respondido AQUI, antes de criar a conta; o registro é feito pelo provider
    // assim que houver sessão (ver useConsent.tsx).
    guardarConsentimentoPendente({ email: email.trim(), birthDate, aceita: aceite, comunicacoes });

    setLoading(true);
    try {
      const res = await dispatch(
        authActions.signUp({ email: email.trim(), password, name: name.trim() }),
      ).unwrap();
      // Anti-enumeração do Supabase: e-mail que JÁ tem conta "passa", com `identities` vazio, e não
      // recebe código.
      if (res.user && Array.isArray(res.user.identities) && res.user.identities.length === 0) {
        setErros((e) => ({ ...e, email: 'Esse e-mail já tem uma conta. Entre na sua conta ou use "Esqueceu a senha?".' }));
        // O erro é do e-mail: volta ao passo dele, onde o campo mostra o erro.
        setEtapa('email');
        setLoading(false);
        return;
      }
      // O gate é a CONFIRMAÇÃO do e-mail, e não a sessão.
      if (res.user?.email_confirmed_at) {
        navigate('/welcome', { replace: true });
      } else {
        // Código já enviado por e-mail: limpa a meia-sessão e pede o código.
        await supabase.auth.signOut();
        setStep('code');
        setLoading(false);
      }
    } catch (err: any) {
      setError(authError(err));
      setLoading(false);
    }
  };

  /** Do e-mail para o resto: só com um endereço válido (o erro aparece no campo). */
  const continuar = () => {
    const e = conferir('email');
    setErros((x) => ({ ...x, email: e }));
    if (!e) setEtapa('dados');
  };

  return {
    step,
    etapa,
    continuar,
    trocarEmail: () => { setEtapa('email'); setError(null); },
    name, setName: (v: string) => { setName(v); reconferir('nome', { name: v }); },
    email, setEmail: (v: string) => { setEmail(v); reconferir('email', { email: v }); },
    password, setPassword: (v: string) => { setPassword(v); reconferir('senha', { password: v }); },
    birthDate, setBirthDate: (v: string) => { setBirthDate(v); reconferir('nascimento', { birthDate: v }); },
    aceite, setAceite: (v: boolean) => { setAceite(v); reconferir('aceite', { aceite: v }); },
    comunicacoes, setComunicacoes,
    loading,
    erros,
    validarCampo,
    error,
    cadastrar,
    aoConfirmar: () => navigate('/welcome', { replace: true }),
  };
};
